import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { discoverAllEvmWallets, connectEvmWallet, pickEvmWallet, setPreferredEvmWalletId, getPreferredEvmWalletId, EVM_WALLET_INSTALL_MSG, proveEvmAddressOwnership } from '../utils/detectEvmWallets';
import { EVM_WALLET_EXAMPLES } from '../utils/evmWalletMessages';
import { approveAgentWithBrowserWallet, approveBuilderFeeWithBrowserWallet } from '../utils/hlAgentSign';
import { fmtSignalTime } from '../utils/fmtPrice';
import TraderCashReserveSettings from './TraderCashReserveSettings';
import PaperTestBalanceSettings from './PaperTestBalanceSettings';
import HlFundingGuide from './HlFundingGuide';
import HlSlippageSettings from './HlSlippageSettings';
import './TraderCashReserveSettings.css';
import './HyperliquidWalletPanel.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const PANEL_VERSION = 'wallet-ui-v5';

function shortAddress(addr) {
  if (!addr || addr.length < 10) return addr || '—';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function walletLabel(w) {
  return w.walletLabel || w.addressMasked || w.maskedAddress || shortAddress(w.walletAddress);
}

function WalletStatusBadges({ wallet, view, isSingleAsset }) {
  if (!wallet) return null;
  const badges = [];

  if (view === 'manual' || view === 'settings') {
    badges.push(
      wallet.manualConsentValid ? (
        <span key="manual" className="hl-wallet-badge hl-wallet-badge-ok">
          Manual linked
        </span>
      ) : (
        <span key="manual" className="hl-wallet-badge hl-wallet-badge-warn">
          Reconfirm required
        </span>
      )
    );
  }

  if (view === 'obot' || view === 'settings') {
    badges.push(
      wallet.obotActive ? (
        <span key="obot" className="hl-wallet-badge hl-wallet-badge-ok">
          oBot active
        </span>
      ) : (
        <span key="obot" className="hl-wallet-badge hl-wallet-badge-muted">
          {isSingleAsset ? 'Linked' : 'oBot off'}
        </span>
      )
    );
  }

  if (view === 'manual' && isSingleAsset) {
    if (wallet.agentApproved) {
      badges.push(
        <span key="agent" className="hl-wallet-badge hl-wallet-badge-ok">
          Agent ✓ — ready to trade
        </span>
      );
    } else {
      badges.push(
        <span key="agent" className="hl-wallet-badge hl-wallet-badge-warn">
          Approve agent to trade
        </span>
      );
    }
  }

  if ((view === 'obot' || view === 'settings') && !isSingleAsset) {
    if (wallet.agentApproved) {
      badges.push(
        <span key="agent" className="hl-wallet-badge hl-wallet-badge-ok">
          Agent ✓ (no key needed)
        </span>
      );
    }
    if (wallet.builderFeeApproved) {
      badges.push(
        <span key="builder" className="hl-wallet-badge hl-wallet-badge-ok">
          Commission ✓
        </span>
      );
    }
  }

  if (view === 'obot' && !wallet.agentApproved) {
    badges.push(
      <span key="key" className="hl-wallet-badge hl-wallet-badge-warn">
        Agent not approved
      </span>
    );
  }

  return <div className="hl-wallet-card-badges">{badges}</div>;
}

function SavedWalletsSection({
  savedWallets,
  activeWalletId,
  onSelectWallet,
  activeWallet,
  view,
  isSingleAsset,
  onCopyAddress,
  copyFeedback,
  selectBusy,
}) {
  return (
    <div className="hl-saved-wallets">
      <div className="hl-saved-wallets-header">
        <div>
          <h3 className="hl-saved-wallets-title">Saved wallets</h3>
          <p className="hl-saved-wallets-sub">
            {savedWallets.length
              ? 'Select the wallet used for manual trades on this page.'
              : 'Connect a browser wallet below to link your first address.'}
          </p>
        </div>
        {savedWallets.length > 0 && (
          <span className="hl-saved-wallets-count">{savedWallets.length}</span>
        )}
      </div>

      {savedWallets.length === 0 ? (
        <div className="hl-saved-wallets-empty">
          <span className="hl-saved-wallets-empty-icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
              <rect x="3" y="6" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M3 10h18" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="16" cy="14" r="1.25" fill="currentColor" />
            </svg>
          </span>
          <p>No wallet linked yet</p>
        </div>
      ) : (
        <ul className="hl-saved-wallets-list" role="listbox" aria-label="Saved wallets">
          {savedWallets.map((w) => {
            const isActive = String(w.id) === String(activeWalletId);
            const label = walletLabel(w);
            return (
              <li key={w.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`hl-wallet-card${isActive ? ' selected' : ''}`}
                  disabled={!!selectBusy}
                  onClick={() => onSelectWallet(w.id)}
                >
                  <span className="hl-wallet-card-avatar" aria-hidden>
                    {label.slice(0, 2).toUpperCase()}
                  </span>
                  <span className="hl-wallet-card-main">
                    <span className="hl-wallet-card-top">
                      <strong className="hl-wallet-card-label">{label}</strong>
                      {isActive && <span className="hl-wallet-card-active-pill">Selected</span>}
                    </span>
                    <span className="hl-wallet-card-addr">{shortAddress(w.walletAddress)}</span>
                    <WalletStatusBadges wallet={w} view={view} isSingleAsset={isSingleAsset} />
                  </span>
                  <span className="hl-wallet-card-check" aria-hidden>
                    {isActive ? (
                      <svg viewBox="0 0 20 20" width="18" height="18">
                        <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.15" />
                        <path
                          d="M6 10l2.5 2.5L14 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <span className="hl-wallet-card-radio" />
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {activeWallet && (
        <div className="hl-wallet-detail">
          <div className="hl-wallet-detail-row">
            <span className="hl-wallet-detail-label">Full address</span>
            <code className="hl-wallet-detail-addr">{activeWallet.walletAddress}</code>
            <button
              type="button"
              className="hl-wallet-copy-btn"
              onClick={() => onCopyAddress(activeWallet.walletAddress)}
              title="Copy address"
            >
              {copyFeedback ? 'Copied' : 'Copy'}
            </button>
          </div>
          {activeWallet.obotActive && activeWallet.obotExpiresAt && view !== 'manual' && (
            <p className="hl-wallet-detail-meta">
              oBot authorized until {activeWallet.obotExpiresAt}
            </p>
          )}
          {view === 'manual' && activeWallet.manualConsentValid && !isSingleAsset && (
            <p className="hl-wallet-detail-meta hl-wallet-detail-meta-ok">
              Daily manual link is active — you can sign trades in your browser wallet.
            </p>
          )}
          {view === 'manual' && isSingleAsset && activeWallet.agentApproved && (
            <p className="hl-wallet-detail-meta hl-wallet-detail-meta-ok">
              Trading agent active — live orders do not need a MetaMask signature each time.
            </p>
          )}
          {view === 'manual' && isSingleAsset && !activeWallet.agentApproved && (
            <p className="hl-wallet-detail-meta hl-wallet-detail-meta-warn">
              Wallet linked — scroll to <strong>Enable live trading</strong> and tap Approve agent (one signature).
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @param {'settings' | 'manual' | 'obot'} view
 *   settings — intro, saved wallets, manual connect (hub Settings; no oBot auth)
 *   manual — saved wallets + browser connect (Manual page gear)
 *   obot — saved wallets + oBot authorization (oBot page gear)
 */
export default function HyperliquidWalletPanel({
  view = 'settings',
  compact = false,
  inModal = false,
  apiPrefix = 'multi-asset',
}) {
  const walletApi = `${API_BASE}/${apiPrefix}`;
  const isSingleAsset = apiPrefix === 'single-asset-hl';
  const showIntro = view === 'settings' || view === 'manual';
  const showSaved = view === 'settings' || view === 'manual' || view === 'obot';
  const showManual = view === 'settings' || view === 'manual';
  const showObot = view === 'obot';

  const [evmWallets, setEvmWallets] = useState([]);
  const [selectedEvmId, setSelectedEvmId] = useState('');
  const [savedWallets, setSavedWallets] = useState([]);
  const [activeWalletId, setActiveWalletId] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectBusy, setSelectBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [durationUnit, setDurationUnit] = useState('hours');
  const [durationValue, setDurationValue] = useState(8);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [pasteAddress, setPasteAddress] = useState('');
  const [showAddByAddress, setShowAddByAddress] = useState(false);

  const loadWallets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${walletApi}/wallets`, { headers: authHeaders() });
      // Per-wallet agentApproved only — never OR with a user-level flag (that painted every card green).
      const list = data.wallets || [];
      setSavedWallets(list);
      const activeAddr = data.activeAddress ? String(data.activeAddress).toLowerCase() : '';
      setActiveWalletId((prev) => {
        if (activeAddr) {
          const match = list.find((w) => String(w.walletAddress || '').toLowerCase() === activeAddr);
          if (match) return String(match.id);
        }
        if (prev && list.some((w) => String(w.id) === String(prev))) return prev;
        return list.length ? String(list[0].id) : '';
      });
    } catch (e) {
      if (e.response?.status !== 404) setError(e.response?.data?.error || e.message);
    }
  }, [walletApi]);

  const selectSavedWallet = useCallback(
    async (walletId) => {
      const id = String(walletId);
      if (!id || id === String(activeWalletId)) return;
      setActiveWalletId(id);
      setSelectBusy(true);
      setError(null);
      try {
        await axios.post(`${walletApi}/wallets/${id}/activate`, {}, { headers: authHeaders() });
        await loadWallets();
        setMessage('Selected wallet is now active for live trading on this desk.');
      } catch (e) {
        setError(e.response?.data?.error || e.message || 'Could not activate wallet');
        await loadWallets();
      } finally {
        setSelectBusy(false);
      }
    },
    [walletApi, activeWalletId, loadWallets]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const wallets = await discoverAllEvmWallets();
      if (cancelled) return;
      setEvmWallets(wallets);
      const preferred = getPreferredEvmWalletId();
      const pick = pickEvmWallet(wallets, preferred);
      if (pick) setSelectedEvmId(pick.id);
    })();
    loadWallets();
    return () => {
      cancelled = true;
    };
  }, [loadWallets]);

  const activeWallet = savedWallets.find((w) => String(w.id) === String(activeWalletId));

  const copyAddress = async (addr) => {
    if (!addr) return;
    try {
      await navigator.clipboard.writeText(addr);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      setError('Could not copy address');
    }
  };

  const saveLinkedWallet = async (address, walletLabel) => {
    const addr = String(address || '').toLowerCase();
    const already = savedWallets.find((w) => String(w.walletAddress || '').toLowerCase() === addr);
    if (already) {
      setActiveWalletId(String(already.id));
      await axios.post(`${walletApi}/wallets/${already.id}/manual-consent`, {}, { headers: authHeaders() });
      setMessage(
        `That MetaMask account is already in Saved wallets (${addr.slice(0, 6)}…${addr.slice(-4)}). It is now Selected.`
      );
      await loadWallets();
      return already.id;
    }
    const { data } = await axios.post(
      `${walletApi}/wallets`,
      { walletAddress: addr, walletLabel: walletLabel || 'MetaMask', hasStoredKey: false },
      { headers: authHeaders() }
    );
    const id = data.wallet?.id;
    if (id) {
      await axios.post(`${walletApi}/wallets/${id}/manual-consent`, {}, { headers: authHeaders() });
    }
    setMessage(`Added ${addr.slice(0, 6)}…${addr.slice(-4)} to Saved wallets — select it above to trade.`);
    await loadWallets();
    if (id) setActiveWalletId(String(id));
    return id;
  };

  const connectForManual = async ({ forceAccountPicker = false } = {}) => {
    const w = pickEvmWallet(evmWallets, selectedEvmId || getPreferredEvmWalletId());
    if (!w) {
      setError(EVM_WALLET_INSTALL_MSG);
      return;
    }
    setBusy(true);
    setError(null);
    setPendingAccounts([]);
    try {
      const { address, accounts } = await connectEvmWallet(w.provider, { forceAccountPicker });
      setPreferredEvmWalletId(w.id);
      setSelectedEvmId(w.id);

      const list = accounts?.length ? accounts : [address];
      const newOnes = list.filter(
        (a) => !savedWallets.some((sw) => String(sw.walletAddress || '').toLowerCase() === a)
      );

      if (forceAccountPicker && newOnes.length === 0) {
        setError(
          `MetaMask still returned ${address.slice(0, 6)}…${address.slice(-4)}, which is already linked. ` +
            `In the MetaMask popup, check the OTHER account (or switch account at the top of MetaMask), then click “Add another MetaMask account” again. ` +
            `Or use “Add by address + sign” below.`
        );
        await saveLinkedWallet(address, w.name);
        return;
      }

      if (list.length > 1 && newOnes.length > 1) {
        setPendingAccounts(newOnes);
        setMessage(`MetaMask exposed ${newOnes.length} new accounts — pick which one to add.`);
        return;
      }

      const toSave = newOnes[0] || address;
      await saveLinkedWallet(toSave, w.name);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const linkPendingAccount = async (addr) => {
    setBusy(true);
    setError(null);
    try {
      const w = pickEvmWallet(evmWallets, selectedEvmId || getPreferredEvmWalletId());
      await saveLinkedWallet(addr, w?.name || 'MetaMask');
      setPendingAccounts([]);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const linkByAddressAndSign = async (e) => {
    e?.preventDefault?.();
    const w = pickEvmWallet(evmWallets, selectedEvmId || getPreferredEvmWalletId());
    if (!w) {
      setError(EVM_WALLET_INSTALL_MSG);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { address } = await proveEvmAddressOwnership(w.provider, pasteAddress);
      setPreferredEvmWalletId(w.id);
      await saveLinkedWallet(address, w.name);
      setPasteAddress('');
      setShowAddByAddress(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const startObot = async () => {
    if (!activeWalletId) {
      setError('Select a saved wallet');
      return;
    }
    if (!activeWallet?.agentApproved) {
      setError('Approve the agent above first — one wallet signature, no private key needed.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/obot/start`,
        {
          walletId: parseInt(activeWalletId, 10),
          durationUnit,
          durationValue: parseInt(durationValue, 10),
        },
        { headers: authHeaders() }
      );
      setMessage(
        `oBot authorized until ${fmtSignalTime(data.expiresAt)} — runs 24/7 via agent wallet.`
      );
      await loadWallets();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const approveAgent = async () => {
    if (!activeWallet) {
      setError('Select a saved wallet first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await approveAgentWithBrowserWallet({
        apiBase: API_BASE,
        authHeaders,
        walletId: activeWallet.id,
        walletAddress: activeWallet.walletAddress,
        apiPrefix: isSingleAsset ? 'single-asset-hl' : 'multi-asset',
      });
      setMessage(
        res.message ||
          (isSingleAsset
            ? 'Agent approved — you can place live trades without signing each order in MetaMask.'
            : 'Agent approved — oBot can now trade without your private key.')
      );
      await loadWallets();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const approveBuilderFee = async () => {
    if (!activeWallet) {
      setError('Select a saved wallet first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await approveBuilderFeeWithBrowserWallet({
        apiBase: API_BASE,
        authHeaders,
        walletId: activeWallet.id,
        walletAddress: activeWallet.walletAddress,
      });
      setMessage(res.message || 'Commission approved — required for signal purchases and live fills.');
      await loadWallets();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const stopObot = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE}/multi-asset/obot/stop`,
        { walletId: activeWalletId ? parseInt(activeWalletId, 10) : undefined },
        { headers: authHeaders() }
      );
      setMessage('oBot stopped — signing key cleared from server memory.');
      await loadWallets();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const reconfirmManual = async () => {
    if (!activeWalletId) return;
    setBusy(true);
    try {
      await axios.post(
        `${walletApi}/wallets/${activeWalletId}/manual-consent`,
        {},
        { headers: authHeaders() }
      );
      setMessage('Manual wallet link confirmed for 24 hours.');
      await loadWallets();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const liquidateAll = async () => {
    if (!window.confirm('Queue close signals for all open positions?')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/multi-asset/liquidate-all`, {}, { headers: authHeaders() });
      setMessage(data.message || `Queued ${data.count} close signal(s).`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const panelTitle = isSingleAsset
    ? 'Single Asset HL wallet'
    : view === 'manual'
      ? 'Manual wallet link'
      : view === 'obot'
        ? 'oBot authorization'
        : 'Your Hyperliquid wallet';

  return (
    <section
      className={`hl-wallet-panel ${compact ? 'compact' : ''} ${inModal ? 'in-modal' : ''}`}
      data-hl-wallet-panel={PANEL_VERSION}
      data-hl-wallet-view={view}
    >
      <h2>{panelTitle}</h2>

      {showIntro && view === 'settings' && (
        <p className="hl-wallet-intro">
          Wallet link and cash reserve for <strong>Manual Trades</strong> and <strong>oBot</strong>.{' '}
          <strong>Manual:</strong> connect any EVM wallet ({EVM_WALLET_EXAMPLES}) — signs each trade in your browser.{' '}
          <strong>oBot:</strong> link the same wallet, approve the agent (one EIP-712 signature) on the oBot page.
        </p>
      )}
      {showIntro && view === 'manual' && !isSingleAsset && (
        <p className="hl-wallet-intro">
          Connect an EVM wallet for manual live trades ({EVM_WALLET_EXAMPLES}). You need at least{' '}
          <strong>$5 USDC</strong> on Hyperliquid — use <strong>Fund Hyperliquid</strong> below. We store only your
          public address; each trade is signed in your wallet. Reconfirm daily.
        </p>
      )}

      {error && <div className="hl-wallet-error">{error}</div>}
      {message && <div className="hl-wallet-success">{message}</div>}

      {!isSingleAsset && view === 'settings' && (
        <>
          <PaperTestBalanceSettings compact />
          <TraderCashReserveSettings compact variant="hub" />
        </>
      )}

      {showSaved && (
        <SavedWalletsSection
          savedWallets={savedWallets}
          activeWalletId={activeWalletId}
          onSelectWallet={selectSavedWallet}
          activeWallet={activeWallet}
          view={view}
          isSingleAsset={isSingleAsset}
          onCopyAddress={copyAddress}
          copyFeedback={copyFeedback}
          selectBusy={selectBusy || busy}
        />
      )}

      {showManual && (
        <div className="hl-wallet-block hl-wallet-connect-block">
          <h3>Manual trading (browser wallet)</h3>
          <p className="hint">
            {isSingleAsset
              ? `Link any EVM wallet (${EVM_WALLET_EXAMPLES}). Already have one linked? Use “Add another MetaMask account” (forces MetaMask to ask again) or “Add by address + sign”.`
              : `Hyperliquid uses EVM wallets (${EVM_WALLET_EXAMPLES}). To add a second MetaMask account use “Add another MetaMask account” or “Add by address + sign”.`}
          </p>
          {evmWallets.length ? (
            <>
              <select
                value={selectedEvmId || evmWallets[0]?.id}
                onChange={(e) => {
                  setSelectedEvmId(e.target.value);
                  setPreferredEvmWalletId(e.target.value);
                }}
              >
                {evmWallets.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
              <button type="button" className="primary-btn" disabled={busy} onClick={() => connectForManual()}>
                Connect & confirm for today
              </button>
              <button
                type="button"
                className="secondary-btn"
                disabled={busy}
                onClick={() => connectForManual({ forceAccountPicker: true })}
                title="Disconnects this site in MetaMask briefly, then opens the account picker so you can choose a different account"
              >
                Add another MetaMask account
              </button>
              <button
                type="button"
                className="secondary-btn"
                disabled={busy}
                onClick={() => setShowAddByAddress((v) => !v)}
              >
                {showAddByAddress ? 'Hide add by address' : 'Add by address + sign'}
              </button>
              {activeWallet && !activeWallet.manualConsentValid && (
                <button type="button" className="secondary-btn" disabled={busy} onClick={reconfirmManual}>
                  Reconfirm daily link
                </button>
              )}
              {pendingAccounts.length > 0 && (
                <div className="hl-wallet-pending-accounts">
                  <p className="hint">Pick which MetaMask account to add:</p>
                  {pendingAccounts.map((a) => (
                    <button
                      key={a}
                      type="button"
                      className="secondary-btn"
                      disabled={busy}
                      onClick={() => linkPendingAccount(a)}
                    >
                      Add {a.slice(0, 6)}…{a.slice(-4)}
                    </button>
                  ))}
                </div>
              )}
              {showAddByAddress && (
                <form className="hl-wallet-add-by-address" onSubmit={linkByAddressAndSign}>
                  <p className="hint">
                    Paste the <strong>0x address</strong> of the MetaMask account you want (copy it from MetaMask →
                    account details). Then MetaMask will ask you to sign — switch to that account in MetaMask before
                    approving the signature.
                  </p>
                  <input
                    type="text"
                    placeholder="0x…"
                    value={pasteAddress}
                    onChange={(e) => setPasteAddress(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button type="submit" className="primary-btn" disabled={busy || !pasteAddress.trim()}>
                    Sign &amp; add to Saved wallets
                  </button>
                </form>
              )}
            </>
          ) : (
            <p className="hint">{EVM_WALLET_INSTALL_MSG}</p>
          )}
        </div>
      )}

      {showManual && !isSingleAsset && activeWallet && !activeWallet.builderFeeApproved && (
        <div className="hl-wallet-block">
          <h3>Platform commission (builder fee)</h3>
          <p className="hint">
            One-time EIP-712 approval in your browser wallet. This authorizes a small platform fee on{' '}
            <strong>live fills</strong> routed through us — you are not charged until you trade. It is also{' '}
            <strong>required before you can buy MAP signal access</strong> on Manual Trades (weekly or monthly
            pass, paid separately in USDC from your Hyperliquid wallet). Approving here does not unlock signals by
            itself.
          </p>
          <button type="button" className="primary-btn" disabled={busy} onClick={approveBuilderFee}>
            Approve commission (builder fee)
          </button>
        </div>
      )}

      {showManual && isSingleAsset && (
        <div className="hl-wallet-block hl-agent-block hl-agent-block-priority">
          <h3>Enable live trading (MetaMask, Rabby, Phantom)</h3>
          <p className="hint">
            <strong>Required for live trades.</strong> One signature authorizes a trade-only agent. After that,
            orders submit without a wallet popup each time. The agent cannot withdraw or transfer funds.
          </p>
          {activeWallet?.agentApproved ? (
            <p className="hl-wallet-detail-meta hl-wallet-detail-meta-ok">
              Agent approved for {shortAddress(activeWallet.walletAddress)}
              {activeWallet.agentValidUntil ? ` (valid until ${activeWallet.agentValidUntil})` : ''} — ready
              to trade.
            </p>
          ) : (
            <button type="button" className="primary-btn" disabled={busy || !activeWallet} onClick={approveAgent}>
              Approve agent — 1 wallet signature
            </button>
          )}
        </div>
      )}

      {showManual && activeWallet?.walletAddress && (
        <HlFundingGuide
          walletAddress={activeWallet.walletAddress}
          variant={isSingleAsset ? 'singleAsset' : 'default'}
          onBalanceChange={() => {
            setMessage('Hyperliquid balance updated.');
          }}
        />
      )}

      {showManual && isSingleAsset && <HlSlippageSettings />}

      {showObot && !isSingleAsset && (
        <div className="hl-wallet-block hl-agent-block">
          <h3>Non-custodial oBot signing (recommended)</h3>
          <p className="hint">
            One signature in your browser wallet authorizes a <strong>trade-only agent key</strong> generated on
            our server. It can place and close trades for oBot but can <strong>never withdraw or transfer</strong>{' '}
            funds (enforced by Hyperliquid itself). Your private key is never typed or stored — and oBot keeps
            running through server restarts.
          </p>
          {activeWallet?.agentApproved ? (
            <p className="hl-wallet-detail-meta hl-wallet-detail-meta-ok">
              Agent approved for {shortAddress(activeWallet.walletAddress)}
              {activeWallet.agentValidUntil ? ` (valid until ${activeWallet.agentValidUntil})` : ''} — no password
              needed to Start oBot.
            </p>
          ) : (
            <button type="button" className="primary-btn" disabled={busy || !activeWallet} onClick={approveAgent}>
              Approve agent — 1 signature, no private key
            </button>
          )}
          {activeWallet?.builderFeeApproved ? (
            <p className="hl-wallet-detail-meta hl-wallet-detail-meta-ok">
              Commission approved (max {activeWallet.builderMaxFeeRate} per fill) — live oBot fills can include
              platform fees.
            </p>
          ) : (
            <button
              type="button"
              className="secondary-btn"
              disabled={busy || !activeWallet}
              onClick={approveBuilderFee}
            >
              Approve commission (builder fee) — required for live fills
            </button>
          )}
        </div>
      )}

      {showObot && !isSingleAsset && (
        <div className="hl-wallet-block hl-obot-block">
          <h3>Start oBot</h3>
          {showSaved && !savedWallets.length && (
            <p className="hint">Link a wallet first, approve the agent above, then Start oBot.</p>
          )}
          {!activeWallet?.agentApproved && activeWallet && (
            <p className="hl-wallet-detail-meta">Approve the agent above before starting oBot.</p>
          )}
          <div className="hl-duration-row">
            <label>Duration</label>
            <input
              type="number"
              min={1}
              value={durationValue}
              onChange={(e) => setDurationValue(e.target.value)}
            />
            <select value={durationUnit} onChange={(e) => setDurationUnit(e.target.value)}>
              <option value="hours">hours</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
          </div>
          <div className="hl-wallet-actions">
            <button type="button" className="primary-btn" disabled={busy} onClick={startObot}>
              Start oBot
            </button>
            <button type="button" className="secondary-btn" disabled={busy} onClick={stopObot}>
              Stop oBot
            </button>
            <button type="button" className="danger-btn" disabled={busy} onClick={liquidateAll}>
              Liquidate all
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
